import { useSetAtom, useAtomValue, useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { poiState, PointOfInterestTab } from '@/components/Analysis/modules/pointOfInterest';
import { EquipmentsTab } from '@/components/Analysis/shared/EquipmentsTab';
import * as analysisState from '@/components/Analysis/state';
import { PointOfInterestChecked, SelectedTab } from '@/types';
import useAbility from '@/hooks/useAbility';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useAllPointOfInterestsForDataLoaderQuery } from '@/__generated__/graphql';
import useLiveSyncAssemblyPois from '@/components/Analysis/shared/hooks/useLiveSyncAssemblyPois';

export const AssemblyTabs = () => {
  const selectedAssemblyId = useAtomValue(analysisState.selectedAssemblyId);
  const selectedPoiId = useAtomValue(poiState.selectedPointOfInterestId);
  const selectedTemplateFilters = useAtomValue(analysisState.selectedTemplateFiltersState);
  const [appliedTemplateFilters, setAppliedTemplateFilters] = useState(selectedTemplateFilters);
  const selectedDeckIds = useAtomValue(analysisState.selectedFilteredDeckIds);
  const [appliedDeckFilters, setAppliedDeckFilters] = useState(selectedDeckIds);
  const selectedPaintRegionIds = useAtomValue(analysisState.selectedFilteredPaintRegionIds);
  const [appliedPaintRegionFilters, setAppliedPaintRegionFilters] =
    useState(selectedPaintRegionIds);
  const selectedWorkpackIds = useAtomValue(analysisState.selectedFilteredWorkpackIds);
  const [appliedWorkpackFilters, setAppliedWorkpackFilters] = useState(selectedWorkpackIds);
  const [applyFilters, setApplyFilters] = useAtom(analysisState.applyFilters);
  const [allPOI, setAllPOI] = useAtom(poiState.structurePois);
  const structureId = useGetStructureId();
  const setPoiFilteredByCheckbox = useSetAtom(poiState.poiFilteredByCheckbox);

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(SelectedTab.COMPONENT);
  const { ability } = useAbility();

  const canReadPOI = ability.can('read', 'PointOfInterest');

  useEffect(() => {
    if (applyFilters) {
      setAppliedTemplateFilters(selectedTemplateFilters);
      setAppliedDeckFilters(selectedDeckIds);
      setAppliedPaintRegionFilters(selectedPaintRegionIds);
      setAppliedWorkpackFilters(selectedWorkpackIds);
    }
  }, [
    applyFilters,
    selectedDeckIds,
    selectedPaintRegionIds,
    selectedTemplateFilters,
    selectedWorkpackIds,
    setAppliedTemplateFilters,
    setAppliedDeckFilters,
    setAppliedPaintRegionFilters,
    setAppliedWorkpackFilters,
  ]);

  const { loading: allPOILoading, refetch: refetchPois } = useAllPointOfInterestsForDataLoaderQuery(
    {
      variables: {
        input: {
          structureId,
          selectedTemplateFilters: appliedTemplateFilters,
          includeManuallyCreated: true,
          selectedDeckIds: appliedDeckFilters,
          paintRegionIds: appliedPaintRegionFilters,
          workpackIds: appliedWorkpackFilters,
        },
      },
      fetchPolicy: 'no-cache',
      skip: !canReadPOI && (!applyFilters || allPOI.length > 0),
      onCompleted(data) {
        if (data?.pointOfInterests) {
          setAllPOI(data.pointOfInterests);
          setApplyFilters(false);
        }
      },
    }
  );

  useLiveSyncAssemblyPois(() => {
    refetchPois().then((response) => {
      if (response.data?.pointOfInterests) {
        setAllPOI(response.data.pointOfInterests);
        setApplyFilters(false);
      }
    });
  });

  const changeTab = useCallback((tabIndex: number) => {
    setSelectedTabIndex(tabIndex);
  }, []);

  useEffect(() => {
    if (selectedPoiId) {
      setSelectedTabIndex(SelectedTab.POINTS_OF_INTEREST);
    }
  }, [selectedPoiId]);

  const pois = useMemo(() => {
    return selectedAssemblyId
      ? allPOI.filter((poi) => selectedAssemblyId === poi.assembly.id)
      : allPOI;
  }, [allPOI, selectedAssemblyId]);

  const tabs = useMemo(() => {
    return [
      {
        index: SelectedTab.COMPONENT,
        label: 'Component',
        component: <EquipmentsTab changeTab={changeTab} />,
        visibilityPermission: true,
      },
      {
        index: SelectedTab.POINTS_OF_INTEREST,
        label: 'Points of Interest',
        component: (
          <PointOfInterestTab
            pois={pois as PointOfInterestChecked[]}
            changeTab={changeTab}
            allPOILoading={allPOILoading}
          />
        ),
        visibilityPermission: canReadPOI,
        testid: 'assembly-poi-tab',
      },
    ].filter((tab) => tab.visibilityPermission);
  }, [allPOILoading, canReadPOI, changeTab, pois]);

  useEffect(() => {
    // initialise the poiFilteredByCheckbox with all pois for this equipment/assembly
    // when an assembly is selected
    setPoiFilteredByCheckbox(pois as PointOfInterestChecked[]);
  }, [pois, setPoiFilteredByCheckbox]);

  //TODO: Will have to remove 'eager' rendering strategy @usman
  return (
    <BasicTabs
      renderingStrategy="eager"
      tabs={tabs}
      selectedTab={selectedTabIndex}
      changeTab={changeTab}
      tabsStyles={{
        '@media (max-width: 1728px)': {
          minHeight: '36px',
          '& button': {
            padding: 0,
            minHeight: '36px',
            minWidth: 0,
          },
        },
      }}
    />
  );
};
